import { Component, OnDestroy, OnInit } from '@angular/core';
import { LayoutService, NavbarBtn } from 'src/app/layout/layout.service';
import { ThirdPartyService } from '../../third-party.service';
import { Subscription } from 'rxjs';
import { FormBuilder, FormGroup } from '@angular/forms';
import { debounceTime, delay, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-service-list',
  templateUrl: './service-list.component.html',
  styleUrls: ['./service-list.component.scss']
})
export class ServiceListComponent implements OnInit, OnDestroy {
  loading: boolean = true;
  summaryLoading: boolean = true;
  services: any[] = [];
  isSummaryExpanded: boolean = false;
  summary: any;
  isExpandFilter: boolean = false;
  is_openSearch: boolean = false;
  subscription = new Subscription;
  searchKeyword: string = '';
  searchForm: FormGroup;
  filterForm: FormGroup;
  isSearchActive: boolean = false;

  technicians: { label: string; value: any }[] = [];
  shopList: any[] = [];
  creditFilterDropdown: any[] = [
    { label: 'All', value: false },
    { label: 'Credit Sales', value: true },
  ];

  params: {
    skip: number;
    take: number;
    isDateChecked: boolean;
    fromDate: string | null;
    toDate: string | null;
    isShopWise: boolean;
    isCustomerWise: boolean;
    isCreditWise: boolean;
    searchKeyWord: string;
    technicianId?: number;
    shopID?: number;
    showCreditOrdersOnly?: boolean;
  } = {
      skip: 0,
      take: 50,
      isDateChecked: false,
      fromDate: null,
      toDate: null,
      isShopWise: false,
      isCustomerWise: false,
      isCreditWise: false,
      searchKeyWord: '',
      technicianId: 0,
      showCreditOrdersOnly: false,
    };

  // lazy load vriables
  throttle = 300;
  scrollDistance = 3;
  scrollUpDistance = 1.5;
  isEndOfList = false;
  isLazyloading: boolean = false;

  constructor(
    public layoutService: LayoutService,
    public thirdPartyService: ThirdPartyService,
    private fb: FormBuilder,
  ) { }

  ngOnInit(): void {
    this.getStaffIdAndBranchId();
    this.populateToolbarData();
    this.setSearchForm();
    this.setFilterForms();
    this.getServiceSalesList();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  public onScroll() {
    if (!this.isLazyloading && !this.isEndOfList) {
      this.isLazyloading = true;
      this.params.skip += this.params.take;
      this.getServiceSalesList();

    }
  }

  public toggleSummary() {
    this.isSummaryExpanded = !this.isSummaryExpanded;
  }

  public clearSearch() {
    this.params.searchKeyWord = "";
    const searchValue = this.searchForm.get('search')?.value;
    this.searchForm.get('search')?.setValue('');
    if (searchValue) {
      this.getServiceSalesList();
    }
  }

  public applyFilter() {
    this.params.technicianId = this.filterForm.get('technician')?.value || 0;
    this.params.shopID = this.filterForm.get('shop')?.value?.BranchID || 0;
    this.params.showCreditOrdersOnly = this.filterForm.get('showCreditSalesOnly')?.value || false;
    this.params.fromDate = this.filterForm.get('fromDate')?.value;
    this.params.toDate = this.filterForm.get('toDate')?.value;

    this.getServiceSalesList();
  }

  public clearFilter() {
    this.setFilterForms();
    this.params.technicianId = 0; 
    this.params.shopID = 0;
    this.params.showCreditOrdersOnly = false;
    this.getServiceSalesList();
  }

  private populateToolbarData() {
    this.layoutService.setToolbar({
      title: 'Service List',
    });

    this.layoutService.setNavbarButtons([
      { action: 'search', state: null, icon: 'search' },
      { action: 'filter', state: null, icon: 'filter_list' },
    ]);

    this.subscription.add(
      this.layoutService.buttonClicked$.subscribe((btn: NavbarBtn) => {

        if (!btn) return;

        switch (btn.action) {
          case 'filter':
            this.isExpandFilter = !this.isExpandFilter;
            break;
          case 'search':
            this.is_openSearch = !this.is_openSearch;
            break;
        }
      })
    );
  }

  private setSearchForm() {
    this.searchForm = this.fb.group({
      search: [""]
    });

    this.subscription.add(this.searchForm.controls['search'].valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      tap(() => this.isSearchActive = true),
      delay(300),
      switchMap((str: string) => {
        this.params.searchKeyWord = str;
        this.params.skip = 0;
        return this.thirdPartyService.getServiceSalesList(this.params);

      })).subscribe((res: any) => {

        this.isSearchActive = false;
        this.services = res.ResponseData;
        this.getServiceSummary();
      }));
  }

  private setFilterForms() {
    let today = new Date();
    let startOfDay = new Date(today);
    startOfDay.setHours(0, 0, 0, 0);
    let endOfDay = new Date(today);
    endOfDay.setHours(23, 59, 59, 999);
    this.params.isDateChecked = true;
    this.params.fromDate = startOfDay.toISOString();
    this.params.toDate = endOfDay.toISOString();
    this.filterForm = this.fb.group({
      fromDate: [this.formatDateForInput(startOfDay)],
      toDate: [this.formatDateForInput(endOfDay)],
      technician: [null],
      shop: [null],
      showCreditSalesOnly: [false],
    });
  }

  private getServiceSalesList() {
    this.loading = true;
    this.thirdPartyService.getServiceSalesList(this.params).subscribe({
      next: (res: any) => {
        this.isLazyloading = false;
        this.services = res.ResponseData;
        this.getServiceSummary();
        if (res.ResponseData.length < this.params.take) {
          this.isEndOfList = true;
        }
        this.loading = false;
      },
      error: (err: any) => {
        this.loading = false;
      }
    });
  }

  private getServiceSummary() {
    this.summaryLoading = true;
    this.thirdPartyService.getServiceSalesListSummary(this.params).subscribe({
      next: (res: any) => {
        if (res?.Result === 'Success' && res?.ResponseData) {
          this.summary = res.ResponseData;
          this.summaryLoading = false;
        }
        this.summaryLoading = false;
      },
      error: (err) => {
        console.error('Error fetching service summary:', err);
        this.summaryLoading = false;
      },
    });
  }

  private getStaffIdAndBranchId() {
    this.thirdPartyService.getStaffIdAndBrandchId().subscribe((res: any) => {
      if (res?.Result === 'Success' && res?.ResponseData) {
        let branchInfo = res.ResponseData;
        this.getAllTechnicians(branchInfo);
        this.getShops(branchInfo);
      }
    });
  }

  public getShops(branchInfo: any) {
    this.thirdPartyService.getShops(branchInfo.UserId).subscribe(
      (res: any) => {
        if (res && res?.Result == 'Success' && res?.ResponseData != 'Failed') {
          this.shopList = res?.ResponseData;
        }
      },
      (error: HttpErrorResponse) => { }
    );
  }

  private getAllTechnicians(branchInfo: any) {
    let params = {
      key: '',
      takeAll: true,
      skip: 0,
      take: 0,
      isDateChecked: false,
      fromDate: null,
      toDate: null,
      branchID: branchInfo.BranchId,
      userType: '',
    };
    this.thirdPartyService.getBranchUsers(params).subscribe((res: any) => {
      if (res?.Result === 'Success' && res?.ResponseData) {
        this.technicians = res?.ResponseData.map((u: any) => ({
          label: u.Name,
          value: u.UserID,
        }));
      }
    });
  }

  private formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;   // Local date — no timezone conversion
  }


}
